import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ruler, Scissors, Layers, Sparkles, ArrowRight, Star, PencilRuler, MessageSquareQuote, Armchair } from "lucide-react";
import SEO from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { IMG, HIGHLIGHTS, COWHIDE } from "@/data/site";
import { api, fileUrl } from "@/lib/api";

const ICONS = { ruler: Ruler, scissors: Scissors, layers: Layers, sparkles: Sparkles };

const ORDER_WAYS = [
  {
    icon: PencilRuler,
    eyebrow: "Design online",
    title: "Build Your Own Cover",
    desc: "Choose a fabric, enter your measurements and preview the look — then we'll review every detail and send your personal quote.",
    image: COWHIDE.photos[0],
    cta: "Build your Own Cover with Tanonshop",
    to: "/covers",
  },
  {
    icon: MessageSquareQuote,
    eyebrow: "Any project",
    title: "Quote-Based Custom",
    desc: "Sofas, RVs, boats, benches or odd shapes — send your measurements, details and photos and we'll craft a personalized quote.",
    image: "https://customer-assets.emergentagent.com/job_tanonshop-preview/artifacts/2ieh46l9_purple%20covers.jpg",
    cta: "Quote-based Custom",
    to: "/custom-order",
  },
  {
    icon: Armchair,
    eyebrow: "Ready-made accents",
    title: "Vintage Throw Pillows",
    desc: "Curated vintage-inspired throw pillow covers — a charming, characterful finishing touch for any room.",
    image: IMG.plantPillows,
    cta: "Vintage Throw Pillow",
    to: "/shop",
  },
];

export default function Home() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/reviews")
      .then(({ data }) => setReviews(data.filter((r) => r.photo).slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO title="Handmade Custom Cushion Covers" description="Tanonshop crafts made-to-measure custom cushion covers for homes, RVs, boats and patios. Over 20 years of handcraftsmanship. Request a free custom quote." />

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img src={IMG.heroLiving} alt="Elegant living room with custom cushions" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-teal-anchor/55" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pb-20 sm:pb-28">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
            <Eyebrow className="text-paper/90">Handcrafted since 2004</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight text-paper">
              Custom cushion covers, cut to fit your life.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-paper/85 leading-relaxed font-light">
              For homeowners, RV & camper owners, and anyone who refuses to settle for
              off-the-shelf. Every cover is measured, cut and sewn by hand.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/custom-order" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 rounded-sm bg-paper px-7 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-teal-anchor transition-all duration-200 hover:-translate-y-px hover:bg-white">
                Request a Custom Quote <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Highlights */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Why Tanonshop</Eyebrow>
            <h2 className="mt-5 font-serif text-4xl lg:text-5xl tracking-tight leading-tight">
              Craftsmanship you can feel in every seam.
            </h2>
            <p className="mt-6 text-teal-anchor/70 leading-relaxed">
              No mass production, no compromise. Just two decades of expertise poured into covers
              that fit perfectly and outlast the furniture underneath them.
            </p>
          </Reveal>
          <div className="lg:col-span-8 grid gap-x-10 gap-y-12 sm:grid-cols-2">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = ICONS[h.icon];
              return (
                <Reveal key={h.title} delay={i * 0.08} className="border-t border-teal-anchor/15 pt-6">
                  <Icon className="text-teal-primary" size={28} strokeWidth={1.5} />
                  <h3 className="mt-5 font-serif text-2xl tracking-tight">{h.title}</h3>
                  <p className="mt-3 text-teal-anchor/70 leading-relaxed">{h.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Three Ways to Order */}
      <section className="bg-paper-soft py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Three ways to order</Eyebrow>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl tracking-tight">Choose how you'd like to begin</h2>
          </Reveal>
          <div className="mt-14 grid gap-8 lg:grid-cols-3">
            {ORDER_WAYS.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} delay={i * 0.1}>
                  <div data-testid={`order-way-${i}`} className="group flex h-full flex-col overflow-hidden rounded-sm border border-teal-anchor/12 bg-white">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={w.image} alt={w.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-7">
                      <Icon className="text-teal-primary" size={26} strokeWidth={1.5} />
                      <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-teal-primary">{w.eyebrow}</p>
                      <h3 className="mt-2 font-serif text-2xl tracking-tight">{w.title}</h3>
                      <p className="mt-3 flex-1 text-sm text-teal-anchor/70 leading-relaxed">{w.desc}</p>
                      <Link
                        to={w.to}
                        data-testid={`order-way-btn-${i}`}
                        className="mt-6 inline-flex items-center justify-center gap-2 rounded-sm bg-teal-primary px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-paper transition-all duration-200 hover:bg-teal-hover hover:-translate-y-px"
                      >
                        {w.cta} <ArrowRight size={15} />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* About teaser split */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal className="order-2 lg:order-1">
            <Eyebrow>The maker</Eyebrow>
            <h2 className="mt-5 font-serif text-4xl lg:text-5xl tracking-tight leading-tight">
              Twenty years at one workbench.
            </h2>
            <p className="mt-6 text-teal-anchor/70 leading-relaxed">
              What began as reupholstering a single family heirloom grew into a lifelong craft.
              Today, that same pair of hands measures, cuts and finishes every cover — treating
              your project the way the trade was meant to be practiced.
            </p>
            <Link to="/about" data-testid="home-about-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-teal-primary link-underline">
              Read the story <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <div className="grid grid-cols-5 gap-4">
              <div className="col-span-3 aspect-[4/5] overflow-hidden">
                <img src={IMG.sewing} alt="Maker sewing fabric" className="h-full w-full object-cover" />
              </div>
              <div className="col-span-2 self-end aspect-[3/4] overflow-hidden">
                <img src={IMG.tools} alt="Sewing tools and thread" className="h-full w-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Recent work & review */}
      <section className="bg-paper-soft py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Recent work &amp; review</Eyebrow>
              <h2 className="mt-4 font-serif text-4xl lg:text-5xl tracking-tight">Straight from our customers</h2>
            </div>
            <Link to="/reviews" className="text-sm font-semibold uppercase tracking-[0.15em] text-teal-primary link-underline">See all reviews</Link>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <article data-testid="home-review-card" className="flex h-full flex-col overflow-hidden rounded-sm border border-teal-anchor/12 bg-white">
                  <div className="aspect-square overflow-hidden">
                    <img src={fileUrl(r.photo)} alt={`${r.name}'s ${r.project_type || "cushion cover"}`} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} size={13} fill={s < r.rating ? "currentColor" : "none"} className={s < r.rating ? "text-teal-primary" : "text-teal-anchor/20"} />
                      ))}
                    </div>
                    <p className="mt-3 flex-1 text-sm text-teal-anchor/75 leading-relaxed">{r.body}</p>
                    <p className="mt-4 font-serif text-lg tracking-tight">{r.name}</p>
                    <p className="text-xs uppercase tracking-[0.13em] text-teal-primary">{[r.project_type, r.date].filter(Boolean).join(" · ")}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="grain relative bg-teal-anchor">
        <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8 py-24 lg:py-32 text-center">
          <div className="flex justify-center gap-1 text-paper/90">
            {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={18} fill="currentColor" />)}
          </div>
          <Reveal as="h2" className="mt-6 font-serif text-4xl lg:text-6xl tracking-tight text-paper">
            Ready for a perfect fit?
          </Reveal>
          <p className="mx-auto mt-5 max-w-xl text-paper/75 leading-relaxed">
            Send your measurements and project details for a free, no-obligation quote.
            We'll respond with fabric options and pricing tailored to you.
          </p>
          <Link to="/custom-order" data-testid="cta-band-quote-btn" className="mt-10 inline-flex items-center gap-2 rounded-sm bg-paper px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-teal-anchor transition-all duration-200 hover:-translate-y-px">
            Request a Custom Quote <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
